import React from 'react';

import isFunction from 'lodash/isFunction';
import isString from 'lodash/isString';
import PropTypes from 'prop-types';

import { makeStyles, alpha } from '@gogame/components';

import AttributeContext from './AttributeContext';
import {
  useAds,
  useCommentsCount,
  useConceptsCount,
  useCreativesCount,
  useVariantsCount,
  useCreativeType,
  useFileExtension,
  useFileResolution,
  useFileSize,
  useFileVideoLength,
  usePartialShared,
  useShared,
  useRating,
  useNew,
  useExpirationDate,
  useExclusiveForAffiliates,
  useLocale,
  useCPFlow,
  usePlatform,
  useConversionPagesCount,
  useAIGenerated,
} from './parsers';
import { AttributeIcon, AttributeLabel, AttributeText } from './types';

const TYPE_ICON = 'icon';
const TYPE_LABEL = 'label';
const TYPE_TEXT = 'text';

const PARSER_ADS = 'ads';
const PARSER_COMMENTS_COUNT = 'commentsCount';
const PARSER_CONCEPTS_COUNT = 'conceptsCount';
const PARSER_CREATIVES_COUNT = 'creativesCount';
const PARSER_VARIANTS_COUNT = 'variantsCount';
const PARSER_CREATIVE_TYPE = 'creativeType';
const PARSER_FILE_EXTENSION = 'fileExtension';
const PARSER_FILE_RESOLUTION = 'fileResolution';
const PARSER_FILE_SIZE = 'fileSize';
const PARSER_FILE_VIDEO_LENGTH = 'fileVideoLength';
const PARSER_PARTIAL_SHARED = 'partialShared';
const PARSER_SHARED = 'shared';
const PARSER_RATING = 'rating';
const PARSER_NEW = 'new';
const PARSER_EXPIRATION_DATE = 'expirationDate';
const PARSER_EXCLUSIVE_FOR_AFFILIATES = 'exclusiveForAffiliates';
const PARSER_LOCALE = 'locale';
const PARSER_CP_FLOW = 'cpFlow';
const PARSER_PLATFORM = 'platform';
const PARSER_CONVERSION_PAGES_COUNT = 'conversionPagesCount';
const PARSER_AI_GENERATED = 'aiGenerated';

const types = {
  [TYPE_ICON]: AttributeIcon,
  [TYPE_LABEL]: AttributeLabel,
  [TYPE_TEXT]: AttributeText,
};

const parsers = {
  [PARSER_ADS]: useAds,
  [PARSER_COMMENTS_COUNT]: useCommentsCount,
  [PARSER_CONCEPTS_COUNT]: useConceptsCount,
  [PARSER_CREATIVES_COUNT]: useCreativesCount,
  [PARSER_VARIANTS_COUNT]: useVariantsCount,
  [PARSER_CREATIVE_TYPE]: useCreativeType,
  [PARSER_FILE_EXTENSION]: useFileExtension,
  [PARSER_FILE_RESOLUTION]: useFileResolution,
  [PARSER_FILE_SIZE]: useFileSize,
  [PARSER_FILE_VIDEO_LENGTH]: useFileVideoLength,
  [PARSER_PARTIAL_SHARED]: usePartialShared,
  [PARSER_SHARED]: useShared,
  [PARSER_RATING]: useRating,
  [PARSER_NEW]: useNew,
  [PARSER_EXPIRATION_DATE]: useExpirationDate,
  [PARSER_EXCLUSIVE_FOR_AFFILIATES]: useExclusiveForAffiliates,
  [PARSER_LOCALE]: useLocale,
  [PARSER_CP_FLOW]: useCPFlow,
  [PARSER_PLATFORM]: usePlatform,
  [PARSER_CONVERSION_PAGES_COUNT]: useConversionPagesCount,
  [PARSER_AI_GENERATED]: useAIGenerated,
};

const useStyles = makeStyles((theme) => {
  return {
    chips: {
      background: '#FFF',
      padding: 4,
      borderRadius: '50%',
    },
    outlined: {
      borderWidth: 1,
      borderStyle: 'solid',
      borderColor: ({ color }) =>
        alpha(
          theme.palette[color]?.main || theme.palette.action.active,
          theme.palette.action.disabledOpacity
        ),
      borderRadius: '16px',
      padding: '3px 7px',
      '&:hover': {
        background: ({ clickable, color }) =>
          clickable
            ? alpha(
                theme.palette[color]?.main || theme.palette.action.active,
                theme.palette.action.hoverOpacity
              )
            : null,
      },
      '&:active': {
        background: ({ clickable, color }) =>
          clickable
            ? alpha(
                theme.palette[color]?.main || theme.palette.action.active,
                theme.palette.action.activatedOpacity
              )
            : null,
      },
      textDecoration: ({ underline }) =>
        underline === 'always' ? 'underline' : underline,
    },
  };
});

const AttributeItem = ({
  value,
  valueParser: valueParserProp,
  icon: iconProp,
  label: labelProp,
  shortLabel: shortLabelProp,
  clickable,
  underline,
  ...props
}) => {
  const contextProps = React.useContext(AttributeContext);

  const {
    type,
    variant: variantProp,
    color: colorProp,
    typographyVariant: typographyVariantProp,
    typographyColor: typographyColorProp,
  } = {
    ...contextProps,
    ...props,
  };

  const typeFactory = React.useMemo(() => {
    if (type) {
      if (isFunction(type) || React.isValidElement(type)) {
        return type;
      } else if (isString(type)) {
        return types[type];
      }
    }
    return ({ value }) => value;
  }, [type]);

  const valueParser = React.useMemo(() => {
    if (valueParserProp) {
      if (
        isFunction(valueParserProp) ||
        React.isValidElement(valueParserProp)
      ) {
        return valueParserProp;
      } else if (isString(valueParserProp)) {
        return parsers[valueParserProp];
      }
    }
    return ({ value }) => ({ label: value });
  }, [valueParserProp]);

  const {
    icon: iconParser,
    label: labelParser,
    shortLabel: shortLabelParser,
    variant: variantParser,
    color: colorParser,
    typographyVariant: typographyVariantParser,
    typographyColor: typographyColorParser,
  } = valueParser({ value });

  const icon = iconProp || iconParser;
  const label = labelProp || labelParser;
  const shortLabel = shortLabelProp || shortLabelParser;
  const variant = variantProp || variantParser;
  const color = colorProp || colorParser;
  const typographyVariant = typographyVariantProp || typographyVariantParser;
  const typographyColor = typographyColorProp || typographyColorParser;

  const classes = useStyles({ color, clickable, underline });

  return React.createElement(typeFactory, {
    className: classes[variant],
    icon,
    label,
    shortLabel,
    color,
    typographyVariant,
    typographyColor,
  });
};

AttributeItem.propTypes = {
  type: PropTypes.oneOf(['icon', 'label', 'text']),
  value: PropTypes.any,
  valueParser: PropTypes.oneOf([
    PropTypes.func,
    'ads',
    'commentsCount',
    'conceptsCount',
    'creativesCount',
    'variantsCount',
    'creativeType',
    'fileExtension',
    'fileResolution',
    'fileSize',
    'fileVideoLength',
    'partialShared',
    'shared',
    'rating',
    'new',
    'expirationDate',
    'exclusiveForAffiliates',
    'locale',
    'cpFlow',
    'platform',
    'conversionPagesCount',
    'aiGenerated',
  ]),
  variant: PropTypes.oneOf(['standard', 'outlined', 'chips']),
  color: PropTypes.string,
  typographyVariant: PropTypes.string,
  typographyColor: PropTypes.string,
  clickable: PropTypes.bool,
  underline: PropTypes.oneOf(['none', 'always']),
};

export default AttributeItem;
